/* eslint max-classes-per-file: "off" */
// TODO: #646 refactor this. Split into separate components. Consider writing separate rows for Conferences & Transcripts
// TODO: #685 cover with tests

import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';
import colors from '@theme/colors';
import ActionHome from 'material-ui/svg-icons/action/info';
import { Td, Row } from '@layout/DashboardTable';
import { TRANSCRIPT_STATUSES, CONFERENCE_STATUSES } from '@config/statuses-constants';
import { MIN_CONFERENCE_DURATION } from '@config/modelsConfig';
import { formatDuration } from '@services/formatTimestamp';
import { Icon, Button, Attendees, OrderNumbers } from '@atoms';
import {
  SummaryPin,
  TranscriptLinks,
  ToggleArrow,
  ConferenceDashboardTooltipMenu,
  CancelledTranscriptTooltip,
} from '@molecules';
import messages from './messages';

const ATTENDEES_DETAILS_NUMBER_OF_COLUMNS = 6;

const StatusWrapper = styled.div`
  white-space: nowrap;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 10px;
    background-color: ${({ status }) => {
      switch (status) {
        case TRANSCRIPT_STATUSES.INITIAL:
        case TRANSCRIPT_STATUSES.UPLOADED:
        case CONFERENCE_STATUSES.WAITING:
        case CONFERENCE_STATUSES.EXPIRED:
          return colors.LIGHT_GREY;
        case TRANSCRIPT_STATUSES.COMPLETE:
        case CONFERENCE_STATUSES.DONE:
          return colors.SUCCESS;
        case CONFERENCE_STATUSES.TALKING:
          return colors.GOLD;
        case TRANSCRIPT_STATUSES.ERROR_UPLOADING:
        case TRANSCRIPT_STATUSES.ERROR_VALIDATION:
        case TRANSCRIPT_STATUSES.ERROR_MERGING:
        case CONFERENCE_STATUSES.CANCELLED:
        case TRANSCRIPT_STATUSES.CANCELLED:
        case TRANSCRIPT_STATUSES.DELETED:
        case CONFERENCE_STATUSES.DELETED:
          return colors.DANGER;
        case TRANSCRIPT_STATUSES.ERROR_DELETE_FROM_TWILIO:
        case TRANSCRIPT_STATUSES.ERROR_UPLOAD_TO_CLOUDSEARCH:
        case TRANSCRIPT_STATUSES.ERROR_EMAIL_SEND:
          return colors.LIGHT_GREY;
        default:
          return 'transparent';
      }
    }};
  }
`;

// TODO: #646 refactor -- try to eliminate underscores in status ids
const TranscriptStatus = ({ status }) => (
  <StatusWrapper status={status}>
    <FormattedMessage
      {...(messages[`transcriptStatus_${status}`] || messages.transcriptStatus_empty)}
    />
  </StatusWrapper>
);
TranscriptStatus.propTypes = {
  status: PropTypes.string.isRequired,
};

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
`;

const ErrorMessage = styled.div`
  position: absolute;
  transform: translate(45%);
`;

const SpeechBubble = styled.div`
  width: 210px;
  box-sizing: border-box;
  padding: 13px;
  position: relative;
  font-size: 12px;
  background: #dadada;
  border-radius: 3px;
  min-height: 50px;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-right-color: #dadada;
    border-left: 0;
    margin-top: -10px;
    margin-left: -10px;
  }
`;

const ErrorHeader = styled.div`
  display: flex;
  margin-bottom: 5px;
  color: ${colors.DANGER};
`;

const ErrorDescription = styled.span`
  font-style: italic;
`;

class StatusCell extends PureComponent {
  propTypes = {
    status: PropTypes.string,
    expanded: PropTypes.bool,
    isSuperAdmin: PropTypes.bool,
    resubmitTranscript: PropTypes.func,
  };

  state = { showErrorHint: false };

  onIconClick = () => {
    this.setState((prevState) => ({ showErrorHint: !prevState.showErrorHint }));
  };

  getErrorHint = (status, showErrorHint) => {
    if (!showErrorHint) return null;

    return (
      <ErrorMessage>
        <SpeechBubble>
          <ErrorHeader>
            <StatusWrapper status={status} />
            {status}
          </ErrorHeader>
          <ErrorDescription>
            Some problems with the file:
            {status}
          </ErrorDescription>
        </SpeechBubble>
      </ErrorMessage>
    );
  };

  render() {
    const { status, expanded, isSuperAdmin, resubmitTranscript } = this.props;
    const { showErrorHint } = this.state;
    const errorHint = this.getErrorHint(status, showErrorHint);
    const iconColor = showErrorHint ? '#D8D8D8' : colors.LIGHT_GREY;
    const iconStyle = { cursor: 'pointer', marginLeft: 10 };
    return (
      <StatusContainer>
        <TranscriptStatus status={status} />
        {status.startsWith('error') && (
          <Fragment>
            <ActionHome color={iconColor} style={iconStyle} onClick={this.onIconClick} />
            {errorHint}
          </Fragment>
        )}
        <div>
          {status === TRANSCRIPT_STATUSES.CANCELLED && expanded && isSuperAdmin && (
            <CancelledTranscriptTooltip resubmitTranscript={resubmitTranscript} />
          )}
        </div>
      </StatusContainer>
    );
  }
}

class DashboardTableRow extends PureComponent {
  static propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    organizationName: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.string),
    date: PropTypes.string.isRequired,
    creationDate: PropTypes.string.isRequired,
    hasBeenSent: PropTypes.bool,
    isTranscriptionFinished: PropTypes.bool,
    user: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }).isRequired,
    showTranscripts: PropTypes.bool,
    resubmitTranscript: PropTypes.func,
    isSuperAdmin: PropTypes.bool,
    pin: PropTypes.string,
    regeneratePinFromDashboard: PropTypes.func,
    isRegenerating: PropTypes.bool,
    cancelConferenceCall: PropTypes.func,
    people: PropTypes.array,
    countries: PropTypes.array,
    status: PropTypes.string,
    isHumanTranscriptionAvailable: PropTypes.bool,
    isAITranscriptionAvailable: PropTypes.bool,
    isAnalyst: PropTypes.bool,
    duration: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    participantCalls: PropTypes.array,
    recordings: PropTypes.array,
    requestHumanTranscript: PropTypes.func,
  };

  static defaultProps = {
    files: [],
    showTranscripts: false,
    recordings: [],
    orderNumbers: [],
  };

  state = {
    expanded: false,
  };

  toggleSummary = () => {
    this.setState(({ expanded }) => ({ expanded: !expanded }));
  };

  render() {
    const {
      id,
      title,
      organizationName,
      date,
      creationDate,
      user,
      pin,
      regeneratePinFromDashboard,
      isRegenerating,
      cancelConferenceCall,
      people,
      countries,
      showTranscripts,
      status,
      isHumanTranscriptionAvailable,
      isAITranscriptionAvailable,
      isAnalyst,
      duration,
      participantCalls,
      recordings,
      requestHumanTranscript,
      isSuperAdmin,
      resubmitTranscript,
      mediaId,
      orderNumbers,
      requestTranscriptCompletionEmail,
      trscHumanTranscriptJobId,
      trscAITranscriptJobId,
    } = this.props;
    const orderNumbersArray = mediaId // select order numbers dependant on which tab we currently are
      ? [mediaId, trscHumanTranscriptJobId, trscAITranscriptJobId] // for transcripts tab
      : [...orderNumbers, trscHumanTranscriptJobId, trscAITranscriptJobId]; // for conferences tab

    const onlineParticipantIds =
      (participantCalls &&
        participantCalls.map((item) => item.attendeeId).filter((item) => !!item)) ||
      [];
    const offlineParticipantNumbers =
      (participantCalls &&
        participantCalls
          .map((item) => !item.attendeeId && item.fromNumber)
          .filter((item) => !!item)) ||
      [];
    const showConferences = !showTranscripts;
    const { expanded } = this.state;
    const analyst = user ? user.email : '...';
    const hideTranscriptToAnalyst = isAnalyst;
    const isConferenceDone = status === CONFERENCE_STATUSES.DONE;
    const isConferenceDeleted = status === CONFERENCE_STATUSES.DELETED;
    const areValidRecordingsNotSentToRev = recordings.some(
      ({ duration: dur, isSentToRev }) => dur >= MIN_CONFERENCE_DURATION && !isSentToRev,
    );
    const canRequestHumanTranscript =
      isConferenceDone && recordings && areValidRecordingsNotSentToRev;
    const tooltipProps = {
      id,
      isConferenceDone,
      isConferenceDeleted,
      canRequestHumanTranscript,
      requestHumanTranscript,
    };

    return (
      <Fragment>
        <Row background expanded={expanded} onClick={this.toggleSummary} status={status}>
          <Td background className="dashboard-row-toggle">
            <ToggleArrow expanded={this.state.expanded} />
          </Td>
          <Td background className="dashboard-row-id">
            {id}
          </Td>
          <Td background className="dashboard-row-organization">
            {organizationName}
          </Td>
          <Td background className="dashboard-row-title">
            {title}
          </Td>
          <Td background className="dashboard-row-duration">
            {formatDuration(duration)}
          </Td>
          <Td background className="dashboard-row-date">
            {moment(date).format('MM/DD/YYYY HH:mm')}
          </Td>
          <Td background className="dashboard-row-creation-date">
            {moment(creationDate).format('MM/DD/YYYY')}
          </Td>
          <Td background className="dashboard-row-status">
            <StatusCell
              status={status}
              expanded={this.state.expanded}
              isSuperAdmin={isSuperAdmin}
              resubmitTranscript={() => resubmitTranscript(id)}
            />
          </Td>
          <Td background className="dashboard-row-analyst">
            {analyst}
          </Td>
          {showTranscripts ? (
            <Td background primary className="dashboard-row-view">
              {hideTranscriptToAnalyst || (
                <TranscriptLinks
                  id={id}
                  status={status}
                  isHumanTranscriptionAvailable={isHumanTranscriptionAvailable}
                  isAITranscriptionAvailable={isAITranscriptionAvailable}
                />
              )}
            </Td>
          ) : (
            <Td background className="dashboard-row-pin">
              {pin}
            </Td>
          )}
          {showConferences && (
            <Td>
              <ConferenceDashboardTooltipMenu {...tooltipProps} />
            </Td>
          )}
        </Row>
        {expanded && (
          <Row background status={status}>
            <Td background />
            <Td background />
            <Td
              background
              className="attendees-details"
              colSpan={ATTENDEES_DETAILS_NUMBER_OF_COLUMNS}
            >
              {orderNumbersArray.length ? (
                <OrderNumbers orders={orderNumbersArray} />
              ) : (
                ''
              )}
              <Attendees
                transcriptId={id}
                requestTranscriptCompletionEmail={requestTranscriptCompletionEmail}
                isAttendeeCallDisabled={isConferenceDeleted}
                people={people}
                countries={countries}
                showTranscripts={showTranscripts}
                callInProgress={status === CONFERENCE_STATUSES.TALKING}
                canRequestTranscriptCompletionEmail={
                  (status === TRANSCRIPT_STATUSES.COMPLETE ||
                    isAITranscriptionAvailable) &&
                  isSuperAdmin
                }
                onlineParticipantIds={onlineParticipantIds}
                offlineParticipantNumbers={offlineParticipantNumbers}
              />
            </Td>
            <Td background className="pin-details" colSpan={showTranscripts ? 1 : 2}>
              {showConferences && !isConferenceDeleted && (
                <SummaryPin
                  pin={pin}
                  regeneratePin={() => regeneratePinFromDashboard(id)}
                  loading={isRegenerating === id}
                />
              )}
            </Td>
            <Td background className="action-buttons">
              {showConferences && pin ? (
                <Button
                  invisible
                  className="cancel-conference"
                  onClick={() => cancelConferenceCall(id)}
                >
                  <Icon name="cross" />
                </Button>
              ) : (
                <Icon name="crossGrey" />
              )}
            </Td>
          </Row>
        )}
      </Fragment>
    );
  }
}

export default DashboardTableRow;



// WEBPACK FOOTER //
// ./components/organisms/DashboardTable/DashboardTableRow.js